
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { checkClientRateLimit } from '@/utils/security';
import { toast } from 'sonner';

export const useSecureUsername = () => {
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateUsername = async (newUsername: string): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to change your username');
      return false;
    }

    // Client-side rate limiting check
    if (!checkClientRateLimit('username_change', 3, 24 * 60 * 60 * 1000)) {
      toast.error('Username change rate limit exceeded. Please wait 24 hours.');
      return false;
    }

    setIsUpdating(true);

    try {
      // Validate username change with our secure function
      const { error: validationError } = await supabase.rpc('validate_username_change', {
        user_id_input: user.id,
        new_username: newUsername
      });

      if (validationError) {
        toast.error(validationError.message);
        return false;
      }

      // Get current username for logging
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      // Log the username change
      await supabase
        .from('username_change_log')
        .insert({
          user_id: user.id,
          old_username: currentProfile?.username || null,
          new_username: newUsername
        });

      // Update the username
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ username: newUsername })
        .eq('id', user.id);

      if (updateError) {
        toast.error('Failed to update username');
        return false;
      }

      toast.success('Username updated successfully');
      return true;

    } catch (error) {
      console.error('Error updating username:', error);
      toast.error('An unexpected error occurred');
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateUsername,
    isUpdating
  };
};
